/**
 * Form single-page de criacao de cargo — /configuracoes/acessos/cargos/novo.
 *
 * Sprint 7 do plano. 4 campos: nome, descricao, parent (cargo pai), exige_mfa.
 * Bloqueio client-side de nome "Master" (reservado). Apos criar, redireciona
 * para /configuracoes/acessos/cargos/<id>.
 */
import React, { useEffect, useState } from "react";
import { FiArrowLeft, FiCheckCircle } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../../context/AuthContext";

import { criarCargo, fetchCargos } from "../api";
import {
  ActionsBar,
  Card,
  CardHeader,
  CardSubtitle,
  CardTitle,
  ErrorMsg,
  Field,
  InlineForm,
  Input,
  PrimaryButton,
  SecondaryButton,
  Select,
  Textarea,
} from "../styles.jsx";

const NOME_RESERVADO = "Master";

function CargoNovo() {
  const navigate = useNavigate();
  const { request } = useAuth();

  const [cargos, setCargos] = useState([]);
  const [dados, setDados] = useState({
    nome: "",
    descricao: "",
    parent_id: "",
    exige_mfa: false,
  });
  const [error, setError] = useState(null);
  const [criando, setCriando] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const payload = await fetchCargos(request, false);
        setCargos(payload?.resultados || []);
      } catch {
        setCargos([]);
      }
    })();
  }, [request]);

  const validar = () => {
    if (!dados.nome.trim()) return "Informe um nome para o cargo.";
    if (dados.nome.trim() === NOME_RESERVADO) {
      return `O nome "${NOME_RESERVADO}" é reservado e não pode ser usado.`;
    }
    if (cargos.some((c) => c.nome.toLowerCase() === dados.nome.trim().toLowerCase())) {
      return "Já existe um cargo com este nome.";
    }
    return null;
  };

  const submeter = async (e) => {
    e.preventDefault();
    setError(null);
    const msg = validar();
    if (msg) {
      setError(msg);
      return;
    }
    setCriando(true);
    try {
      const payload = {
        nome: dados.nome.trim(),
        descricao: dados.descricao,
        parent_id: dados.parent_id || null,
        exige_mfa: dados.exige_mfa,
      };
      const resp = await criarCargo(request, payload);
      const cargoCriado = resp?.data || resp;
      if (cargoCriado?.id) {
        navigate(`/configuracoes/acessos/cargos/${cargoCriado.id}`);
      } else {
        navigate("/configuracoes/acessos/cargos");
      }
    } catch (err) {
      setError(err?.message || "Erro ao criar cargo.");
    } finally {
      setCriando(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>Novo cargo</CardTitle>
          <CardSubtitle>
            Crie um cargo (ex.: Coordenador, Comprador) e atribua suas permissões
            depois na ficha.
          </CardSubtitle>
        </div>
        <SecondaryButton type="button" onClick={() => navigate("/configuracoes/acessos/cargos")}>
          <FiArrowLeft size={14} /> Cancelar
        </SecondaryButton>
      </CardHeader>

      {error && <ErrorMsg>{error}</ErrorMsg>}

      <InlineForm onSubmit={submeter}>
        <Field className="full">
          Nome do cargo
          <Input
            autoFocus
            required
            value={dados.nome}
            onChange={(e) => setDados((p) => ({ ...p, nome: e.target.value }))}
            placeholder="ex.: Coordenador de PCP"
          />
        </Field>
        <Field className="full">
          Descrição (opcional)
          <Textarea
            value={dados.descricao}
            onChange={(e) => setDados((p) => ({ ...p, descricao: e.target.value }))}
            placeholder="O que esse cargo faz?"
          />
        </Field>
        <Field>
          Cargo pai (opcional)
          <Select
            value={dados.parent_id}
            onChange={(e) => setDados((p) => ({ ...p, parent_id: e.target.value }))}
          >
            <option value="">Sem cargo pai</option>
            {cargos.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nome}
              </option>
            ))}
          </Select>
        </Field>
        <Field>
          MFA obrigatório
          <label
            style={{
              display: "inline-flex",
              gap: 8,
              alignItems: "center",
              fontSize: "0.85rem",
              fontWeight: 400,
              textTransform: "none",
              color: "inherit",
              marginTop: 6,
            }}
          >
            <input
              type="checkbox"
              checked={dados.exige_mfa}
              onChange={(e) => setDados((p) => ({ ...p, exige_mfa: e.target.checked }))}
            />
            Exigir MFA para usuários neste cargo
          </label>
        </Field>
        <ActionsBar className="full" style={{ justifyContent: "flex-end" }}>
          <SecondaryButton
            type="button"
            onClick={() => navigate("/configuracoes/acessos/cargos")}
            disabled={criando}
          >
            Cancelar
          </SecondaryButton>
          <PrimaryButton type="submit" disabled={criando}>
            <FiCheckCircle size={14} /> Criar cargo
          </PrimaryButton>
        </ActionsBar>
      </InlineForm>
    </Card>
  );
}

export default CargoNovo;
